import React, { useState } from 'react';
import GlobalDialog from '../../../components/mui/Dialogue/GlobalDialog';
import { AddButton } from '../../../components/mui/Buttons/Buttons';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Box, Button, CircularProgress, Grid, Stack, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import useHandleApiResponse from '../../../Hooks/useHandleApiResponse';
import { getFeeForecastData, UpdateBulkExpectedSessions } from '../../../redux/features/feeForecastSlice';
import dayjs from 'dayjs';

// One labeled count in the results popup's summary row (Processed/Added/Skipped/Rejected).
const ResultStat = ({ label, value, color }) => (
  <Grid item xs={6} sm={3}>
    <Box sx={{ textAlign: 'center' }}>
      <Typography variant="h5" fontWeight="700" color={color || 'text.primary'}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  </Grid>
);

const AddBulkExpectedSessions = ({ dispatchParams, date }) => {
  const [open, setOpen] = useState(false);
  const { manualForecast, loading, feeForecast, expectedSessionLoading } = useSelector((state) => state.feeForecast);
  const { meta, data } = feeForecast;
  const [file, setFile] = useState(null);
  // Backend already returns { total, success, skipped, failed, errors } per bulk upload
  // (bulkUpsertExpectedSessions) - this just captures and displays it, nothing new to compute.
  const [resultsOpen, setResultsOpen] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const dispatch = useDispatch();
  const handleApiResponse = useHandleApiResponse();

  const finalParams = { ...dispatchParams, limit: 'no', isBulkExpectedSessions: true };

  const downloadCSV = () => {
    dispatch(getFeeForecastData(finalParams)).then((res) => {
      handleApiResponse(res, {
        onSuccess: () => {
          if (!res.payload.data || res.payload.data === 0) return;

          // getAdmissionRegister has no flat "final_expected_session" field - only nested
          // forecast.final_sessions / forecast.expected_sessions_student. Without adding it here,
          // the exported CSV never has the column this dialog's own instructions tell the user to
          // edit, so handleUpload always reads it back as undefined -> 0 for every row.
          const rows = res.payload.data.map((row) => ({
            ...row,
            final_expected_session:
              row.forecast?.final_sessions ?? row.forecast?.expected_sessions_student ?? row.current_month_expected_sessions ?? 0,
          }));

          const headers = Object.keys(rows[0]);

          const csvRows = [
            headers.join(','),
            ...rows.map((row) => headers.map((field) => `"${row[field] ?? ''}"`).join(',')),
          ];

          const csvString = csvRows.join('\n');

          const blob = new Blob([csvString], {
            type: 'text/csv;charset=utf-8;',
          });

          const url = URL.createObjectURL(blob);

          const link = document.createElement('a');
          link.href = url;
          link.download = 'bulk_expected_sessions.csv';

          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        },
        onFailed: () => {
          console.log('Something went wrong');
        },
      });
    });
  };

  const parseCSV = (text) => {
    const rows = text.split('\n').map((row) => row.split(','));
    const headers = rows[0];

    const data = rows.slice(1).map((row) => {
      let obj = {};

      headers.forEach((header, index) => {
        obj[header.replace(/"/g, '')] = row[index]?.replace(/"/g, '') || '';
      });

      return obj;
    });
    console.log('data-=-=-=-', data);

    return data.filter((item) => Object.values(item).some((v) => v !== ''));
  };

  /* ---------------- CSV UPLOAD ---------------- */

  const handleUpload = () => {
    if (!file) return;

    const reader = new FileReader();

    reader.onload = (e) => {
      const text = e.target.result;

      const entries = parseCSV(text);
      const filteredEntries = entries.map((item) => ({
        course_enrollment_id: item.course_enrollment_id,
        expectedSessions: item.final_expected_session,
      }));

      dispatch(
        UpdateBulkExpectedSessions({
          year: date ? dayjs(date).format('YYYY') : '',
          month: date ? dayjs(date).format('MM') : '',
          entries: filteredEntries,
        }),
      ).then((res) => {
        handleApiResponse(res, {
          onSuccess: () => {
            setOpen(false);
            dispatch(getFeeForecastData(dispatchParams));

            setFile(null);
            setUploadResults(res.payload.data || null);
            setResultsOpen(true);
          },
          onFailed: () => {
            console.log('Something went wrong');
          },
        });
      });
    };

    reader.readAsText(file);
  };

  return (
    <>
    <GlobalDialog
      title={'Bulk Upload — Expected Sessions'}
      open={open}
      setOpen={setOpen}
      fullWidth
      maxWidth={'sm'}
      closeButton={true}
      popupButton={
        <AddButton size={'small'} action={() => setOpen(true)}>
          Bulk Upload — Expected Sessions
        </AddButton>
      }
      content={
        <>
          <Stack spacing={3}>
            <Typography variant="body2" color="text.secondary">
              For students already on a Term or Percentage forecast (a course package is selected). Download the current data CSV, edit the <b>final_expected_session</b> column in the file, and upload the updated CSV below. For students on a Manual forecast, use "Bulk Upload — Manual Forecast Fees" instead.
            </Typography>
            {/* DOWNLOAD CSV */}
            <Button
              startIcon={
                loading ? <CircularProgress size={20} /> : <Icon icon="line-md:download-loop" width="24" height="24" />
              }
              variant="contained"
              onClick={downloadCSV}
              disabled={loading}
            >
              Download CSV for Editing
            </Button>
            {/* FILE SELECT */}
            <Button
              startIcon={<Icon icon="material-symbols:upload" width="24" height="24" />}
              variant="outlined"
              component="label"
            >
              Select CSV File
              <input
                hidden
                type="file"
                accept=".csv"
                onChange={(e) => setFile(e.target.files[0])}
              />
            </Button>

            {file && (
              <Typography variant="body2">
                Selected file: <b>{file.name}</b>
              </Typography>
            )}

            {/* UPLOAD BUTTON */}
            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={expectedSessionLoading || !file}
              startIcon={expectedSessionLoading ? <CircularProgress size={20} /> : null}
            >
              {expectedSessionLoading ? 'Uploading...' : 'Upload CSV'}
            </Button>
          </Stack>
        </>
      }
    />
    <GlobalDialog
      title="Bulk Upload Results"
      open={resultsOpen}
      setOpen={setResultsOpen}
      fullWidth
      maxWidth={'sm'}
      closeButton={true}
      popupButton={null}
      content={
        <Stack spacing={2}>
          <Grid container spacing={1}>
            <ResultStat label="Processed" value={uploadResults?.total ?? 0} />
            <ResultStat label="Added / Updated" value={uploadResults?.success ?? 0} color="success.main" />
            <ResultStat label="Skipped (No Change)" value={uploadResults?.skipped ?? 0} color="text.secondary" />
            <ResultStat label="Rejected" value={uploadResults?.failed ?? 0} color="error.main" />
          </Grid>

          {uploadResults?.errors?.length > 0 && (
            <Alert severity="error">
              {uploadResults.errors.length} row{uploadResults.errors.length > 1 ? 's' : ''} rejected:
              <Box sx={{ maxHeight: 220, overflowY: 'auto', mt: 1, fontSize: '0.8rem' }}>
                {uploadResults.errors.map((err, idx) => (
                  <div key={idx}>
                    • Enrollment {err.courseEnrollmentId}: {err.error}
                  </div>
                ))}
              </Box>
            </Alert>
          )}

          {uploadResults?.sample?.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Sample of saved rows, to confirm your numbers actually landed:
              </Typography>
              <Box sx={{ mt: 0.5, fontSize: '0.8rem' }}>
                {uploadResults.sample.map((row, idx) => (
                  <div key={idx}>
                    • {row.studentName || `Enrollment ${row.courseEnrollmentId}`}
                    {row.admissionNo ? ` (${row.admissionNo})` : ''}
                    {row.courseName ? ` — ${row.courseName}` : ''} → {row.expectedSessions} sessions
                  </div>
                ))}
              </Box>
            </Box>
          )}
        </Stack>
      }
      actionButton={<Button onClick={() => setResultsOpen(false)}>Close</Button>}
    />
  </>
  );
};

export default AddBulkExpectedSessions;
